'use client';

import { useEffect, useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import type { RootState } from '@/store/store';
import { useNavigate } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';

import AlreadyAppliedDialog from '@/shared/components/dialogs/AlreadyAppliedDialog';

import {
  ALREADY_APPLIED_MESSAGE,
  isAlreadyAppliedErrorMessage,
} from '@/modules/candidate/constants/candidateApplication.constants';

import { useApplication } from '@/modules/candidate/hooks/useApplication';

import {
  getResumePrefillStatusThunk,
} from '@/modules/candidate/store/applicationThunk';

import { setDraftData } from '@/modules/candidate/store/applicationSlice';

import type {
  CandidateApplicationDialogProps,
  CandidateApplicationStep,
} from '@/modules/candidate/types/candidateApplication.types';

import StepIndicator from './StepIndicator';

import ResumeUploadStep from './steps/ResumeUploadStep';

import ResumeParsingStep from './steps/ResumeParsingStep';

import ProfileReviewStep from './steps/ProfileReviewStep';

import VideoUploadStep from './steps/VideoUploadStep';

import SuccessStep from './steps/SuccessStep';

import { useResumeUpload } from '@/modules/candidate/hooks/useResumeUpload';

import { useVideoCapture } from '@/modules/candidate/hooks/useVideoCapture';

import { useCandidateProfileForm } from '@/modules/candidate/hooks/useCandidateProfileForm';


const RESUME_PREFILL_POLL_INTERVAL = 1000;

const MAX_RESUME_PREFILL_POLL_ATTEMPTS = 120;


export default function CandidateApplicationDialog({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  source = 'CAREER_PAGE',
  referralToken,
  referralUtm,
}: CandidateApplicationDialogProps) {
  const dispatch = useDispatch();

  const user = useSelector(
    (state: RootState) => state.auth.user
  );

  const accountProfile = {
    fullName: user?.name ?? '',
    email: user?.email ?? '',
  };

  const navigate = useNavigate();


  const {
    createDraft,
    uploadResume,
    uploadVideo,
    submitDraft,
    draftId,
    updateCandidateProfileAPI,
    resetApplication: resetApplicationState,
  } = useApplication();


  const resumeUpload =
    useResumeUpload();


  const videoCapture =
    useVideoCapture();


  const profileForm =
    useCandidateProfileForm();


  const [currentStep, setCurrentStep] =
    useState<CandidateApplicationStep>(
      'resume-upload'
    );


  const [parsingProgress, setParsingProgress] =
    useState(0);


  const [isSubmitting, setIsSubmitting] =
    useState(false);


  const [
    showAlreadyAppliedPopup,
    setShowAlreadyAppliedPopup,
  ] = useState(false);


  const [
    alreadyAppliedMessage,
    setAlreadyAppliedMessage,
  ] = useState(
    ALREADY_APPLIED_MESSAGE
  );


  const resetApplication = () => {
    setCurrentStep(
      'resume-upload'
    );

    setParsingProgress(0);

    setIsSubmitting(false);

    resumeUpload.reset();

    videoCapture.reset();

    profileForm.reset();

    resetApplicationState();
  };


  useEffect(() => {
    if (open && !draftId) {
      resetApplication();
    }
  }, [open, jobId]);


  const handleDialogChange = (
    nextOpen: boolean
  ) => {
    if (
      !nextOpen &&
      (
        currentStep ===
        'resume-parsing' ||
        isSubmitting
      )
    ) {
      return;
    }

    videoCapture.stopCamera();

    onOpenChange(nextOpen);

    if (!nextOpen) {
      resetApplication();
    }
  };


  const handleAlreadyAppliedPopupChange = (
    nextOpen: boolean
  ) => {
    setShowAlreadyAppliedPopup(
      nextOpen
    );

    if (!nextOpen) {
      onOpenChange(false);

      resetApplication();
    }
  };


  const handleViewApplicationStatus = () => {
    console.log(
      'Redirecting candidate to application status:',
      alreadyAppliedMessage
    );

    setShowAlreadyAppliedPopup(
      false
    );

    videoCapture.stopCamera();

    onOpenChange(false);

    resetApplication();

    navigate('/candidate');
  };


  /*
   * Poll the backend worker until resume prefill
   * reaches a terminal state.
   *
   * We use the same Redux thunk that updates the
   * application slice, while also returning the
   * response here so the dialog can immediately
   * prefill the candidate form.
   */
  const pollResumePrefill = async (
    targetDraftId: string
  ) => {
    let consecutiveErrors = 0;

    for (
      let attempt = 0;
      attempt <
      MAX_RESUME_PREFILL_POLL_ATTEMPTS;
      attempt += 1
    ) {
      try {
        const response: any =
          await dispatch(
            getResumePrefillStatusThunk({
              draftId: targetDraftId,
            }) as any
          );

        if (
          response?.meta?.requestStatus !==
          'fulfilled'
        ) {
          consecutiveErrors += 1;

          if (
            consecutiveErrors >= 5
          ) {
            throw new Error(
              response?.payload ||
              'Unable to check resume processing status.'
            );
          }

          await new Promise(
            (resolve) =>
              window.setTimeout(
                resolve,
                RESUME_PREFILL_POLL_INTERVAL
              )
          );

          continue;
        }

        consecutiveErrors = 0;

        const data =
          response?.payload?.data;

        if (!data) {
          throw new Error(
            'Invalid resume prefill status response.'
          );
        }

        const progress =
          Number(
            data.resumePrefillProgress
          );

        if (
          Number.isFinite(progress)
        ) {
          setParsingProgress(
            Math.min(
              100,
              Math.max(
                0,
                progress
              )
            )
          );
        }


        /*
         * Worker completed successfully.
         */
        if (
          data.resumePrefillStatus ===
          'COMPLETED'
        ) {
          setParsingProgress(100);

          return data;
        }


        /*
         * Worker failed.
         *
         * Resume upload itself has already succeeded.
         * Therefore do not force the candidate to re-upload
         * the resume. Continue to profile review so the
         * candidate can enter/verify the information manually.
         */
        if (
          data.resumePrefillStatus ===
          'FAILED'
        ) {
          return data;
        }


        /*
         * QUEUED / PROCESSING / IDLE
         */
        await new Promise(
          (resolve) =>
            window.setTimeout(
              resolve,
              RESUME_PREFILL_POLL_INTERVAL
            )
        );
      } catch (error) {
        console.error(
          '[RESUME PREFILL] Polling error:',
          error
        );

        throw error;
      }
    }


    throw new Error(
      'Resume processing is taking longer than expected. Please review your profile manually.'
    );
  };


  const handleResumedDraft = async (
    draft: any
  ) => {
    dispatch(
      setDraftData(draft)
    );


    /*
     * Existing completed prefill result:
     * directly populate profile and continue.
     */
    if (
      draft.resumePrefillStatus ===
      'COMPLETED' &&
      draft.resumeParsedData
    ) {
      profileForm.prefillCandidateProfile(
        draft.resumeParsedData,
        accountProfile
      );

      setParsingProgress(100);

      setCurrentStep(
        'profile-review'
      );

      return true;
    }


    /*
     * Existing queued/processing prefill:
     * resume the polling flow instead of
     * immediately opening the profile step.
     */
    if (
      draft.resumePrefillStatus ===
      'QUEUED' ||
      draft.resumePrefillStatus ===
      'PROCESSING'
    ) {
      setCurrentStep(
        'resume-parsing'
      );

      setParsingProgress(
        draft.resumePrefillProgress ??
        0
      );

      const prefillResult =
        await pollResumePrefill(
          draft.id
        );

      if (
        prefillResult?.resumePrefillStatus ===
        'COMPLETED'
      ) {
        setCurrentStep(
          'profile-review'
        );

        return true;
      }

      /*
       * Failed prefill should not block
       * manual profile completion.
       */
      if (
        prefillResult?.resumePrefillStatus ===
        'FAILED'
      ) {
        setCurrentStep(
          'profile-review'
        );

        profileForm.setProfileError(
          prefillResult.resumePrefillError ||
          'Resume details could not be prefilled. Please review and complete your profile manually.'
        );

        return true;
      }
    }


    /*
     * Existing failed/legacy draft:
     * preserve the old flow and allow manual
     * profile completion.
     */
    const step =
      draft.currentStep;


    if (
      step === 2 ||
      step === 3
    ) {
      setCurrentStep(
        'profile-review'
      );

      return true;
    }


    if (
      step === 4
    ) {
      /*
       * If parsed data exists, use it before
       * entering the video step.
       */
      if (
        draft.resumeParsedData
      ) {
        profileForm.prefillCandidateProfile(
          draft.resumeParsedData,
          accountProfile
        );
      }

      setCurrentStep(
        'video-upload'
      );

      return true;
    }


    return false;
  };


  const parseResume = async () => {
    if (
      !resumeUpload.selectedResume
    ) {
      resumeUpload.setResumeError(
        'Please upload your resume before continuing.'
      );

      return;
    }


    try {
      resumeUpload.setResumeError(
        null
      );

      setParsingProgress(0);

      setCurrentStep(
        'resume-parsing'
      );


      /*
       * Create or resume the draft.
       */
      const draftResponse: any =
        await createDraft(
          jobId,
          source,
          referralToken,
          referralUtm
        );


      if (
        draftResponse?.meta?.requestStatus !==
        'fulfilled'
      ) {
        const errorMessage =
          typeof draftResponse?.payload ===
            'string'
            ? draftResponse.payload
            : draftResponse?.payload
              ?.message ||
            draftResponse?.error
              ?.message ||
            'Unable to create application draft.';


        const isAlreadyApplied =
          isAlreadyAppliedErrorMessage(
            errorMessage
          );


        if (isAlreadyApplied) {
          setParsingProgress(0);

          setCurrentStep(
            'resume-upload'
          );

          setAlreadyAppliedMessage(
            String(
              errorMessage
            )
          );

          setShowAlreadyAppliedPopup(
            true
          );

          return;
        }


        throw new Error(
          String(errorMessage)
        );
      }


      const draftData =
        draftResponse.payload.data;

      const draft =
        draftData.draft;



      /*
       * New draft.
       */
      /*
  * Upload the selected resume.
  * Works for both a newly-created draft and
  * an existing/resumed draft.
  */
      const targetDraftId = draft.id;

      if (!targetDraftId) {
        throw new Error('Application draft ID is missing.');
      }

      profileForm.reset();

      setParsingProgress(0);


      /*
       * Upload the selected resume.
       * The backend queues the real RESUME_PREFILL
       * worker after successful upload.
       */
      const uploadResponse: any =
        await uploadResume(
          targetDraftId,
          resumeUpload.selectedResume
        );


      if (
        uploadResponse?.meta?.requestStatus !==
        'fulfilled'
      ) {
        throw new Error(
          uploadResponse?.payload ??
          'Resume upload failed.'
        );
      }


      /*
       * Read the actual backend status instead of
       * simulating parsing progress.
       */
      const uploadedDraft =
        uploadResponse?.payload
          ?.data
          ?.draft;


      if (
        uploadedDraft
      ) {
        setParsingProgress(
          uploadedDraft.resumePrefillProgress ??
          0
        );
      }


      const prefillResult =
        await pollResumePrefill(
          targetDraftId
        );


      /*
       * Successful AI prefill.
       */
      if (
        prefillResult?.resumePrefillStatus ===
        'COMPLETED'
      ) {
        if (
          prefillResult.resumeParsedData
        ) {
          profileForm.prefillCandidateProfile(
            prefillResult.resumeParsedData,
            accountProfile
          );
        }

        setParsingProgress(100);

        setCurrentStep(
          'profile-review'
        );

        return;
      }


      /*
       * AI prefill failed.
       *
       * Candidate can still continue manually;
       * the resume upload itself remains valid.
       */
      if (
        prefillResult?.resumePrefillStatus ===
        'FAILED'
      ) {
        setParsingProgress(
          prefillResult.resumePrefillProgress ??
          0
        );

        setCurrentStep(
          'profile-review'
        );

        profileForm.setProfileError(
          prefillResult.resumePrefillError ||
          'Resume details could not be prefilled. Please review and complete your profile manually.'
        );

        return;
      }


      /*
       * Defensive fallback.
       */
      setCurrentStep(
        'profile-review'
      );
    } catch (error: any) {
      console.error(
        '[RESUME PREFILL] Resume processing error:',
        error
      );

      setCurrentStep(
        'resume-upload'
      );

      setParsingProgress(0);

      resumeUpload.setResumeError(
        error?.message ??
        'Unable to process your resume.'
      );
    }
  };


  const continueToVideoStep = () => {
    const validationError =
      profileForm.validateCandidateProfile();


    if (validationError) {
      profileForm.setProfileError(
        validationError
      );

      return;
    }


    profileForm.setProfileError(
      null
    );

    setCurrentStep(
      'video-upload'
    );
  };


  const handleSubmitApplication =
    async () => {
      const payload =
        profileForm.buildApplicationPayload(
          draftId
        );


      const currentDraftId =
        draftId;


      if (!currentDraftId) {
        videoCapture.setVideoError(
          "We couldn't find your application. Please restart the application process and try again."
        );

        return;
      }


      if (
        !videoCapture.selectedVideo
      ) {
        videoCapture.setVideoError(
          'Please upload or record your video introduction before submitting your application.'
        );

        return;
      }


      videoCapture.setVideoError(
        null
      );

      setIsSubmitting(true);


      try {
        const profileResponse: any =
          await updateCandidateProfileAPI({
            ...payload,
            draftId:
              currentDraftId,
          });


        if (
          profileResponse?.meta?.requestStatus !==
          'fulfilled'
        ) {
          throw new Error(
            profileResponse?.payload ||
            'Profile update failed.'
          );
        }


        const videoResponse: any =
          await uploadVideo(
            currentDraftId,
            videoCapture.selectedVideo
          );


        if (
          videoResponse?.meta?.requestStatus !==
          'fulfilled'
        ) {
          throw new Error(
            videoResponse?.payload ||
            "Your video couldn't be uploaded."
          );
        }


        const submitResponse: any =
          await submitDraft(
            currentDraftId
          );


        if (
          submitResponse?.meta?.requestStatus !==
          'fulfilled'
        ) {
          const errorMessage =
            submitResponse?.payload ||
            'Application submission failed.';


          const isAlreadyApplied =
            isAlreadyAppliedErrorMessage(
              errorMessage
            );


          if (isAlreadyApplied) {
            setAlreadyAppliedMessage(
              errorMessage
            );

            setShowAlreadyAppliedPopup(
              true
            );

            return;
          }


          throw new Error(
            errorMessage
          );
        }


        setCurrentStep(
          'success'
        );
      } catch (error: any) {
        console.error(
          'Submit Application Error:',
          error
        );


        videoCapture.setVideoError(
          error?.message ||
          'Something went wrong while submitting your application. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    };


  const activeStepIndex =
    currentStep === 'resume-upload' ||
      currentStep === 'resume-parsing'
      ? 0
      : currentStep === 'profile-review'
        ? 1
        : 2;


  return (
    <>
      <Dialog
        open={
          open &&
          !showAlreadyAppliedPopup
        }
        onOpenChange={
          handleDialogChange
        }
      >
        <DialogContent className="max-h-[92vh] w-[95vw] max-w-4xl overflow-y-auto p-0">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Apply for {jobTitle}
            </DialogTitle>

            <DialogDescription>
              Complete the application steps below.
            </DialogDescription>
          </DialogHeader>


          {currentStep === 'success' ? (
            <SuccessStep
              jobTitle={jobTitle}
              onDone={() =>
                handleDialogChange(
                  false
                )
              }
            />
          ) : (
            <>
              <StepIndicator
                activeStepIndex={
                  activeStepIndex
                }
              />


              {currentStep ===
                'resume-upload' && (
                  <ResumeUploadStep
                    fileInputRef={
                      resumeUpload.fileInputRef
                    }
                    selectedResume={
                      resumeUpload.selectedResume
                    }
                    resumeError={
                      resumeUpload.resumeError
                    }
                    isDragging={
                      resumeUpload.isDragging
                    }
                    onFileChange={
                      resumeUpload.handleFileChange
                    }
                    onDragOver={
                      resumeUpload.handleDragOver
                    }
                    onDragLeave={
                      resumeUpload.handleDragLeave
                    }
                    onDrop={
                      resumeUpload.handleDrop
                    }
                    onRemoveResume={
                      resumeUpload.handleRemoveResume
                    }
                    onNext={
                      parseResume
                    }
                  />
                )}


              {currentStep ===
                'resume-parsing' && (
                  <ResumeParsingStep
                    parsingProgress={
                      parsingProgress
                    }
                    resumeName={
                      resumeUpload
                        .selectedResume
                        ?.name
                    }
                  />
                )}


              {currentStep ===
                'profile-review' && (
                  <ProfileReviewStep
                    candidateProfile={
                      profileForm.candidateProfile
                    }
                    profileError={
                      profileForm.profileError
                    }
                    profileFieldErrors={
                      profileForm.profileFieldErrors
                    }
                    onFieldChange={
                      profileForm.updateCandidateProfile
                    }
                    onBack={() =>
                      setCurrentStep(
                        'resume-upload'
                      )
                    }
                    onNext={
                      continueToVideoStep
                    }
                  />
                )}


              {currentStep ===
                'video-upload' && (
                  <VideoUploadStep
                    videoFileInputRef={
                      videoCapture.videoFileInputRef
                    }
                    liveVideoRef={
                      videoCapture.liveVideoRef
                    }
                    videoTab={
                      videoCapture.videoTab
                    }
                    selectedVideo={
                      videoCapture.selectedVideo
                    }
                    videoPreviewUrl={
                      videoCapture.videoPreviewUrl
                    }
                    isVideoDragging={
                      videoCapture.isVideoDragging
                    }
                    videoError={
                      videoCapture.videoError
                    }
                    isCameraActive={
                      videoCapture.isCameraActive
                    }
                    cameraError={
                      videoCapture.cameraError
                    }
                    isRecording={
                      videoCapture.isRecording
                    }
                    recordingSeconds={
                      videoCapture.recordingSeconds
                    }
                    isSubmitting={
                      isSubmitting
                    }
                    onTabChange={
                      videoCapture.handleVideoTabChange
                    }
                    onFileChange={
                      videoCapture.handleVideoFileChange
                    }
                    onDragOver={
                      videoCapture.handleVideoDragOver
                    }
                    onDragLeave={
                      videoCapture.handleVideoDragLeave
                    }
                    onDrop={
                      videoCapture.handleVideoDrop
                    }
                    onRemoveVideo={
                      videoCapture.handleRemoveVideo
                    }
                    onStartCamera={
                      videoCapture.startCamera
                    }
                    onStartRecording={
                      videoCapture.startRecording
                    }
                    onStopRecording={
                      videoCapture.stopRecording
                    }
                    onRetake={
                      videoCapture.retakeVideo
                    }
                    formatRecordingTime={
                      videoCapture.formatRecordingTime
                    }
                    onBack={() => {
                      videoCapture.stopCamera();

                      setCurrentStep(
                        'profile-review'
                      );
                    }}
                    onSubmit={
                      handleSubmitApplication
                    }
                  />
                )}
            </>
          )}
        </DialogContent>
      </Dialog>


      <AlreadyAppliedDialog
        open={
          showAlreadyAppliedPopup
        }
        message={
          alreadyAppliedMessage
        }
        onOpenChange={
          handleAlreadyAppliedPopupChange
        }
        onViewApplicationStatus={
          handleViewApplicationStatus
        }
      />
    </>
  );
}